import { useCallback, useState, useEffect } from 'react'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

/**
 * Tour hook for DayOff Create form
 * Uses custom tour-styles for consistent styling across all tours
 */
export const useDayOffTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentTourStep, setCurrentTourStep] = useState(0)

    const tourSteps: TourStep[] = [
        {
            selector: '#day-off-type',
            title: 'Loại phiếu nghỉ',
            description:
                'Chọn loại nghỉ phép: "Nghỉ không lương" hoặc "Nghỉ phép năm". Nghỉ phép năm sẽ trừ vào số ngày phép còn lại của bạn.',
        },
        {
            selector: '#day-off-reason',
            title: 'Lý do nghỉ phép',
            description:
                'Nhập lý do nghỉ phép. Trường này bắt buộc phải điền để gửi phiếu nghỉ.',
        },
        {
            selector: '#day-off-range',
            title: 'Thời gian nghỉ',
            description:
                'Chọn ngày và buổi nghỉ (Cả ngày, Sáng, hoặc Chiều). Bạn có thể thêm nhiều ngày nghỉ bằng nút + bên dưới.',
        },
        {
            selector: '#employee-day-off-left',
            title: 'Số ngày phép còn lại',
            description:
                'Đây là số ngày phép năm còn lại của bạn. Khi tạo phiếu nghỉ phép năm, số này sẽ được cập nhật theo thời gian thực.',
        },
    ]

    const showTourUI = (step: TourStep, stepIndex: number) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found
            if (stepIndex < tourSteps.length - 1) {
                goToStep(stepIndex + 1)
            } else {
                endTour()
            }
            return
        }

        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Wait for scroll then add popover
        setTimeout(() => {
            const popover = createTourPopover({
                title: step.title,
                description: step.description,
                currentStep: stepIndex + 1,
                totalSteps: tourSteps.length,
                onPrev:
                    stepIndex > 0 ? () => goToStep(stepIndex - 1) : undefined,
                onNext: () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        goToStep(stepIndex + 1)
                    }
                },
                onClose: endTour,
                isLastStep: stepIndex === tourSteps.length - 1,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()
        showTourUI(step, stepIndex)
        setCurrentTourStep(stepIndex)
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
    }, [])

    const startTour = useCallback(() => {
        setIsActive(true)
        setCurrentTourStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourUI(tourSteps[0], 0)
        }, 300)
    }, [])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startTour, endTour, isActive, currentStep: currentTourStep }
}

export default useDayOffTour
